import React, { createContext, useContext, useState, useCallback } from 'react';
import { CITY_SLUG, ALL_AREAS_LABEL, AREA_BY_SLUG, nearestArea } from '../config/geo';

const LocationContext = createContext(null);
const COOKIE = 'as_loc';

const readCookie = () => {
  try {
    const m = document.cookie.match(/(?:^|; )as_loc=([^;]+)/);
    if (!m) return null;
    return JSON.parse(decodeURIComponent(m[1]));
  } catch (e) {
    return null;
  }
};

const writeCookie = (loc) => {
  try {
    const v = encodeURIComponent(JSON.stringify(loc));
    document.cookie = `${COOKIE}=${v}; max-age=${60 * 60 * 24 * 30}; path=/; samesite=lax`;
  } catch (e) {
    /* ignore */
  }
};

const defaultLoc = () => {
  const saved = readCookie();
  if (saved && saved.city) {
    return {
      city: saved.city || CITY_SLUG,
      area: saved.area || null,
      areaName: saved.areaName || ALL_AREAS_LABEL,
      lat: saved.lat ?? null,
      lng: saved.lng ?? null,
      ts: saved.ts || Date.now(),
    };
  }
  return { city: CITY_SLUG, area: null, areaName: ALL_AREAS_LABEL, lat: null, lng: null, ts: Date.now() };
};

export const LocationProvider = ({ children }) => {
  const [loc, setLoc] = useState(defaultLoc);

  const persist = useCallback((next) => {
    setLoc(next);
    writeCookie(next);
  }, []);

  const setArea = useCallback((slug) => {
    const a = AREA_BY_SLUG[slug];
    if (!a) return;
    persist({ city: CITY_SLUG, area: a.slug, areaName: a.name, lat: a.lat, lng: a.lng, ts: Date.now() });
  }, [persist]);

  const setAllAreas = useCallback(() => {
    persist({ city: CITY_SLUG, area: null, areaName: ALL_AREAS_LABEL, lat: null, lng: null, ts: Date.now() });
  }, [persist]);

  const detect = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not available'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = Math.round(pos.coords.latitude * 100) / 100;
          const lng = Math.round(pos.coords.longitude * 100) / 100;
          const a = nearestArea(lat, lng);
          const next = { city: CITY_SLUG, area: a.slug, areaName: a.name, lat, lng, ts: Date.now() };
          persist(next);
          resolve(next);
        },
        (err) => reject(err),
        { enableHighAccuracy: false, timeout: 8000 }
      );
    });
  }, [persist]);

  return (
    <LocationContext.Provider value={{ ...loc, setArea, setAllAreas, detect }}>
      {children}
    </LocationContext.Provider>
  );
};

export const useLocationPref = () => {
  const ctx = useContext(LocationContext);
  if (!ctx) throw new Error('useLocationPref must be used within LocationProvider');
  return ctx;
};

export default LocationContext;
